/* eslint-env browser */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter, routerShape } from 'react-router';

import {
  Container,
  Drawer,
  Overlay,
} from 'rebass';
import { Helmet } from 'react-helmet-async';

import { isBetaHost } from 'shared/util';

import actions from 'frontend/state/actions';
import { selectors as userSelectors } from 'frontend/state/reducers/user';
import { selectors as siteSelectors } from 'frontend/state/reducers/site';

import PageHelmet from 'frontend/components/PageHelmet';

import If from './If';
import Header from './Header';
import Banner from './Banner';
import Footer from './Footer';
import JobList from './JobList';
import AuthPanel from './Auth';
import DefaultErrorBoundary from './DefaultErrorBoundary';
import SiteOverlay from './SiteOverlay';
import TalentTreeOverlay from './TalentTreeOverlay';

import config from '../config';

class RaidbotsApp extends React.Component {
  static displayName = 'RaidbotsApp';

  static propTypes = {
    children: PropTypes.node.isRequired,
    user: PropTypes.shape({
      username: PropTypes.string,
      stripeLinked: PropTypes.bool,
    }).isRequired,
    loggedIn: PropTypes.bool,
    isBetaSite: PropTypes.bool,

    showHeader: PropTypes.bool,
    showFooter: PropTypes.bool,
    showBanner: PropTypes.bool,

    onLoginSuccess: PropTypes.func.isRequired,
    fetchSims: PropTypes.func.isRequired,
    fetchStatus: PropTypes.func.isRequired,

    router: routerShape.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
  };

  static childContextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    rebass: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    reflexbox: PropTypes.object,
  };


  static defaultProps = {
    loggedIn: false,

    showHeader: true,
    showFooter: true,
    showBanner: true,

    isBetaSite: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      drawerOpen: false,
      loginOpen: false,
    };

    this.toggleDrawer = this.toggleDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.openLogin = this.openLogin.bind(this);
    this.closeLogin = this.closeLogin.bind(this);

    this.onRegister = this.onRegister.bind(this);
    this.onLogin = this.onLogin.bind(this);
  }

  // eslint-disable-next-line class-methods-use-this
  getChildContext() {
    return {
      rebass: config,
      reflexbox: {
        breakpoints: {
          sm: '(min-width: 30em)',
          md: '(min-width: 48em)',
          lg: '(min-width: 59em)',
        },
      },
    };
  }

  componentDidMount() {
    if (this.props.loggedIn) {
      this.props.fetchSims();
    }
    if (!isBetaHost(_.get(window, 'location.hostname'))) {
      this.props.fetchStatus();
    }
  }

  onRegister(user) {
    this.closeLogin();
    this.props.onLoginSuccess(user);
    this.props.router.push('#logged-in');
  }

  onLogin(user) {
    this.closeLogin();
    this.props.onLoginSuccess(user);
    this.props.fetchSims();
    this.props.router.push('#logged-in');
  }

  closeDrawer() {
    this.setState({ drawerOpen: false });
  }

  toggleDrawer() {
    const { drawerOpen } = this.state;
    this.setState({ drawerOpen: !drawerOpen });
  }

  openLogin() {
    this.setState({ loginOpen: true });
  }

  closeLogin() {
    this.setState({ loginOpen: false });
  }

  render() {
    const {
      showHeader,
      showFooter,
      showBanner,
      isBetaSite,
    } = this.props;

    let siteBackground = config.colors.gray;
    if (isBetaSite) {
      siteBackground = '#443339';
    }

    return (
      <DefaultErrorBoundary>
        <div
          style={{
            fontFamily: config.fontFamily,
            fontWeight: config.fontWeight,
            letterSpacing: config.letterSpacing,
            color: config.color,
            backgroundColor: siteBackground,
            height: '100%',
            minHeight: '100%',
          }}
        >
          <Helmet meta={[{ name: 'viewport', content: 'width=device-width, initial-scale=1' }]} />
          <PageHelmet page="home" />
          <SiteOverlay />
          <TalentTreeOverlay />
          <If
            condition={showHeader}
            then={() => (
              <Header
                onJobsClick={this.toggleDrawer}
                onLoginClick={this.openLogin}
              />
            )}
          />
          <Container style={{ maxWidth: 1200, minHeight: '100%', paddingBottom: '229px', paddingTop: '75px' }}>
            <If
              condition={showBanner && this.props.location.pathname !== '/blingtron'}
              then={() => (
                <Banner />
              )}
            />
            {this.props.children}
          </Container>
          <If
            condition={showFooter}
            then={() => (
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  clear: 'both',
                  marginTop: '-247px',
                }}
              >
                <Footer />
              </div>
            )}
          />
          <Drawer
            open={this.state.drawerOpen}
            onDismiss={this.toggleDrawer}
            position="right"
            pt={4}
          >
            <JobList onJobClick={this.closeDrawer} />
          </Drawer>
          <Overlay
            open={this.state.loginOpen}
            onDismiss={this.closeLogin}
            style={{
              overflow: 'none',
            }}
          >
            <AuthPanel
              onRegister={this.onRegister}
              onLogin={this.onLogin}
              onRequestClose={this.closeLogin}
            />
          </Overlay>
        </div>
      </DefaultErrorBoundary>
    );
  }
}

const mapStateToProps = state => ({
  user: userSelectors.user(state),
  loggedIn: userSelectors.isLoggedIn(state),
  isBetaSite: siteSelectors.isBetaSite(state),
});

const mapDispatchToProps = {
  onLoginSuccess: actions.onLoginSuccess,
  flashMessage: actions.flashMessage,
  fetchSims: actions.fetchSims,
  fetchStatus: actions.fetchStatus,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RaidbotsApp));
